import { ChangeEvent, useEffect, useState } from "react";

// material ui
import { Typography, TextField, InputAdornment, CircularProgress } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { styled } from "@mui/material/styles";

type DisplayMode = "both" | "labelOnly" | "titleOnly" | "none";
// Parameters
const SET_SPINNER_DELAY = 200;

type TextFieldProps = {
  id?: string;
  title: string;
  label?: string;
  showLabel?: boolean;
  showTitle?: boolean;
  displayMode?: DisplayMode;
  fullWidth: boolean;
  value?: string;
  inputRef?: any;
  disabled?: boolean;
  loading?: boolean;
  dontDisableOnShowSpinner?: boolean;
  inputAdornment?: string;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  type?: string;
  helperText?: string | React.ReactNode;
  error?: boolean;
  required?: boolean;
  placeholder?: string;
  handleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  sx?: {};
};

const Input = styled(TextField)(({ theme }) => ({
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
}));

const TextFieldInput = ({
  id,
  title,
  label,
  value,
  inputRef,
  showTitle,
  showLabel,
  displayMode,
  fullWidth,
  disabled,
  loading,
  dontDisableOnShowSpinner,
  inputAdornment,
  multiline,
  rows,
  minRows,
  maxRows,
  type,
  helperText,
  error,
  required,
  placeholder,
  handleChange,
  sx,
}: TextFieldProps) => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (loading) {
      timer = setTimeout(() => {
        setShowSpinner(true);
      }, SET_SPINNER_DELAY);
    } else {
      setShowSpinner(false);
      if (timer) clearTimeout(timer);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading]);

  return (
    <>
      {(
        displayMode
          ? displayMode === "both" || displayMode === "titleOnly"
          : (!showLabel || showTitle)
      ) && (
          <Typography variant="h5" sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}

      <FormControl fullWidth={fullWidth} sx={sx}>
        <Input
          id={id ?? `outlined-basic-auto-` + title}
          label={
            displayMode
              ? (displayMode === "both" || displayMode === "labelOnly"
                ? (label ?? title)
                : undefined)
              : (showLabel ? (label ?? title) : undefined)
          }
          helperText={helperText}
          inputRef={inputRef}
          disabled={disabled || (showSpinner && !dontDisableOnShowSpinner)}
          value={value}
          onChange={handleChange}
          color="secondary"
          multiline={multiline}
          placeholder={placeholder}
          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
          rows={rows}
          minRows={minRows}
          maxRows={maxRows}
          type={type}
          error={error}
          required={required}
          InputProps={{
            endAdornment: inputAdornment ? (
              <InputAdornment position="end">{inputAdornment}</InputAdornment>
            ) : null,
            startAdornment: showSpinner ? (
              <InputAdornment position="end">
                <CircularProgress size={16} color="secondary" />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            bgcolor: "white",
            borderRadius: 1,
            "& label": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: value
                ? "100%"
                : `calc(100% - ${inputAdornment
                  ? typeof inputAdornment === "string"
                    ? inputAdornment.length > 5
                      ? "80px"
                      : "50px"
                    : "56px"
                  : "20px"
                })`,
              marginTop: value !== "" ? "0px" : "-5px",
              "&.Mui-focused": {
                maxWidth: "100%", // Adjust according to the actual space available when focused
                marginTop: "0px",
              },
            },
            "& .MuiInputLabel-shrink": {
              borderRadius: 1,
            },
            "& .MuiOutlinedInput-input": {
              pr: 0,
            },
            "& .MuiInputAdornment-root": {
              ml: 0.2,
            },
            "& .MuiOutlinedInput-root": {
              ...(multiline && {
                padding: "10.5px 12px !important",
              }),
            },
          }}
        />
      </FormControl>
    </>
  );
};

export default TextFieldInput;
